/** Supabase yokken model görseli / marka logosu — tarayıcıda kalıcı (localStorage) */

const STORAGE_KEY = 'automobile_catalog_overrides_v1';

export type AutomobileLocalOverrides = {
  modelImages: Record<string, string>;
  brandLogos: Record<string, string>;
  modelYears: Record<string, number[]>;
};

const EMPTY: AutomobileLocalOverrides = { modelImages: {}, brandLogos: {}, modelYears: {} };

export function modelImageKey(brandSlug: string, modelName: string): string {
  return `${brandSlug}|${modelName}`;
}

export function modelYearsKey(brandSlug: string, modelDisplayName: string): string {
  return `${brandSlug}|${modelDisplayName}`;
}

export function loadAutomobileLocalOverrides(): AutomobileLocalOverrides {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as AutomobileLocalOverrides;
    return {
      modelImages: parsed.modelImages || {},
      brandLogos: parsed.brandLogos || {},
      modelYears: parsed.modelYears || {},
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveAutomobileLocalOverrides(data: AutomobileLocalOverrides): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function setLocalModelImage(brandSlug: string, modelName: string, imageUrl: string): void {
  const data = loadAutomobileLocalOverrides();
  const key = modelImageKey(brandSlug, modelName);
  if (imageUrl.trim()) data.modelImages[key] = imageUrl.trim();
  else delete data.modelImages[key];
  saveAutomobileLocalOverrides(data);
}

export function setLocalBrandLogo(brandSlug: string, logoUrl: string): void {
  const data = loadAutomobileLocalOverrides();
  if (logoUrl.trim()) data.brandLogos[brandSlug] = logoUrl.trim();
  else delete data.brandLogos[brandSlug];
  saveAutomobileLocalOverrides(data);
}

export function setLocalModelYears(brandSlug: string, modelDisplayName: string, years: number[]): void {
  const data = loadAutomobileLocalOverrides();
  const key = modelYearsKey(brandSlug, modelDisplayName);
  const cleaned = years
    .map((y) => Number(y))
    .filter((y) => Number.isFinite(y) && y >= 1885 && y <= 2100)
    .map((y) => Math.trunc(y));
  const uniq = Array.from(new Set(cleaned)).sort((a, b) => a - b);
  if (uniq.length) data.modelYears[key] = uniq;
  else delete data.modelYears[key];
  saveAutomobileLocalOverrides(data);
}

export function getLocalModelYears(brandSlug: string, modelDisplayName: string): number[] | null {
  const data = loadAutomobileLocalOverrides();
  const key = modelYearsKey(brandSlug, modelDisplayName);
  const years = data.modelYears?.[key];
  return Array.isArray(years) && years.length ? years : null;
}

export function exportOverridesJson(): string {
  return JSON.stringify(loadAutomobileLocalOverrides(), null, 2);
}

type AnyOverrides = Partial<AutomobileLocalOverrides> & {
  model_images?: Record<string, string>;
  brand_logos?: Record<string, string>;
  model_years?: Record<string, number[]>;
};

function normalizeImported(raw: AnyOverrides | null | undefined): AutomobileLocalOverrides {
  const modelImages = (raw?.modelImages || raw?.model_images || {}) as Record<string, string>;
  const brandLogos = (raw?.brandLogos || raw?.brand_logos || {}) as Record<string, string>;
  const modelYears = (raw?.modelYears || raw?.model_years || {}) as Record<string, number[]>;

  const cleanedYears: Record<string, number[]> = {};
  for (const [k, v] of Object.entries(modelYears || {})) {
    if (!Array.isArray(v)) continue;
    const years = v
      .map((y) => Number(y))
      .filter((y) => Number.isFinite(y) && y >= 1885 && y <= 2100)
      .map((y) => Math.trunc(y));
    const uniq = Array.from(new Set(years)).sort((a, b) => a - b);
    if (uniq.length) cleanedYears[k] = uniq;
  }

  return {
    modelImages: modelImages || {},
    brandLogos: brandLogos || {},
    modelYears: cleanedYears,
  };
}

export function importOverridesJson(jsonText: string, mode: 'merge' | 'replace' = 'merge'): void {
  const parsed = JSON.parse(jsonText) as AnyOverrides;
  const incoming = normalizeImported(parsed);
  const current = loadAutomobileLocalOverrides();
  const next =
    mode === 'replace'
      ? incoming
      : {
          modelImages: { ...current.modelImages, ...incoming.modelImages },
          brandLogos: { ...current.brandLogos, ...incoming.brandLogos },
          modelYears: { ...current.modelYears, ...incoming.modelYears },
        };
  saveAutomobileLocalOverrides(next);
}
